import styles from './ProductCard.module.scss'
import React from 'react'
import Button from '@/components/buttons/Button/Button'
import CartIcon from '@/assets/svg/cart.svg'
import BannerIcon from '@/assets/svg/banner.svg'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import useAddToCart from '@/utils/hooks/useAddToCart'
import usePrice from '@/utils/hooks/usePrice'
import NoiseLevelIcon from '@/assets/svg/noise-level.svg'
import FuelIcon from '@/assets/svg/fuel.svg'
import RainIcon from '@/assets/svg/rain.svg'
import WinterIcon from '@/assets/svg/winter.svg'
import { ArticleModel } from '@/types/api'
import Link from 'next/link'
import { convertToURL } from '@/utils/helpers'
import { useRouter } from 'next/router'
import StockStatus from '@/components/article/StockStatus/StockStatus'
import useAnalyticsManager from '@/utils/hooks/useAnalyticsManager'
import SearchMetaData from '@/types/SearchMetaData'
import useLocalizedLink from '@/utils/hooks/useLocalizedLink'

type Props = {
  item: ArticleModel,
  listId: string,
  listName: string,
  metaData?: SearchMetaData,
  showSelectButton?: boolean,
  onSelect?: (item: ArticleModel) => void
}

const ProductCard = ({
  item,
  listId,
  listName,
  metaData,
  showSelectButton = false,
  onSelect,
}: Props) => {
  const { t } = useTranslation()
  const { addToCart } = useAddToCart()
  const { price } = usePrice()
  const router = useRouter()
  const analyticsManager = useAnalyticsManager()
  const getLocalizedLink = useLocalizedLink()

  const onClickBuy = () => {
    addToCart({
      articleId: item.id,
      quantity: item.defaultQuantity,
      kType: metaData?.kType,
      licensePlate: metaData?.licensePlate,
      articleText: item.articleText,
      price: item.price,
    })
  }

  const onClickSelect = () => {
    if (onSelect)
      onSelect(item)
  }

  const onClickInfo = () => {
    analyticsManager.clickProduct(item, listId, listName)
  }

  const onClickImage = () => {
    onClickInfo()
    router.push(productLink)
  }

  let productLink = `${getLocalizedLink(item.productLink)}`

  if (metaData?.kType)
    productLink += `?ktype=${metaData.kType}&licenseplate=${metaData.licensePlate}`

  return (
    <article className={styles.component}>
      <div className={styles.main}>
        <div className={styles.title}>
          <h3>{item.brandName}</h3>
          <p>{item.articleText}</p>
        </div>
        {item.isNew && (
          <div className={styles.banner}>
            <BannerIcon />
            <div>{t('search.news')}</div>
          </div>
        )}
        <div className={styles.image}>
          {item.imageId && (
            <Image
              src={`${process.env.NEXT_PUBLIC_API}/images/${item.imageId}`}
              alt={item.brandName}
              width={160}
              height={160}
              style={{ objectFit: 'contain' }}
              loading="lazy"
              onClick={onClickImage}
            />
          )}
        </div>
        <div className={styles.buyInfo}>
          <div className={styles.price}>
            <div className={styles.currentPrice}>{price(item.price, true)}</div>
            {item.regularPrice && (
              <div className={styles.regularPrice}>
                {t('shop:regularPrice')} {price(item.regularPrice, true)}
              </div>
            )}
          </div>
          <div className={styles.buttons}>
            <Link href={productLink}>
              <Button
                darkGradient={true}
                rounded={true}
                label={t('search.info')}
                arrow={true}
                onClick={onClickInfo}
              />
            </Link>
            {showSelectButton ? (
              <Button
                rounded={true}
                label={t('select')}
                onClick={onClickSelect}
              />
            ) : (
              <Button
                rounded={true}
                label={t('search.buy')}
                icon={CartIcon}
                iconPosition="right"
                onClick={onClickBuy}
              />
            )}
          </div>
        </div>
      </div>
      <div className={styles.bottom}>
        {item.fuelEfficiency && (
          <div className={styles.properties}>
            <FuelIcon /> {item.fuelEfficiency}
          </div>
        )}
        {item.noiceValue && (
          <div className={styles.properties}>
            <NoiseLevelIcon /> {item.noiceValue}
          </div>
        )}
        {item.wetGrip && (
          <div className={styles.properties}>
            <RainIcon /> {item.wetGrip}
          </div>
        )}
        {item.isWinterApproved && (
          <div className={styles.properties}>
            <WinterIcon />
          </div>
        )}
        <StockStatus
          article={item}
          light={true}
        />
      </div>
    </article >
  )
}

export default ProductCard